import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { setIsLoading } from "../../slices/reportSlice";
import * as services from "../../services/reportApi";
import { generateRandomString } from "utils/helper";
import { useDispatch, useSelector } from "react-redux";

function usePND3Report() {
    const dispatch = useDispatch();
    const [search, setSearch] = useState({});
    const [showReport, setShowReport] = useState(false);
    const [searchValues, setSearchValues] = useState({});
    const [pdfUrl, setPdfUrl] = useState(null);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isDownload, setIsDownload] = useState(false);
    const [isDownloadDoc, setIsDownloadDoc] = useState(false);
    const [isDownloadZip, setIsDownloadZip] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const previewPND3ReportPdf = useCallback(async (params = {}, changeYear) => {
        try {
            dispatch(setIsLoading(true));

            const response = await services.ExportPND3ReportPdf({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...params,
                ...params.pagination,
                ...search,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
                year: params.year,
                month: params?.month?.format?.("MM") || params?.month
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            setPdfUrl(url);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [])

    const exportPND3ReportDocument = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadDoc(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND3ReportDocumentPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Cover-Page-PND3.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadDoc(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND3Report = useCallback(
        async (params = {}, changeYear) => {
            setIsDownload(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND3ReportPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Attachment-PND3.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownload(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND3ReportZip = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadZip(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND3ReportZip({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "PND3.zip");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadZip(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const handleSearch = (values) => {
        const selectedYear = values.year?.year() || dayjs().year();
        setShowReport(true);
        setSearchValues(values);
        setSearch({ year: selectedYear, month: values.month });
        previewPND3ReportPdf({
            year: selectedYear,
            month: values.month
        });
    };

    return {
        handleSearch,
        showReport,
        pdfUrl,
        isModalOpen,
        setIsModalOpen,
        showModal,
        exportPND3ReportDocument,
        exportPND3Report,
        exportPND3ReportZip,
        searchValues,
        isDownload,
        isDownloadDoc,
        isDownloadZip
    }
}

export default usePND3Report;